import type { ApiResponse } from '@easiibill/shared';

/** Error returned by the easiibill API (or a network failure, with status 0). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue | QueryValue[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

let businessId: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** Every tenant-scoped request carries the active business; the server re-checks membership. */
export function setApiBusiness(id: string | null): void {
  businessId = id;
}

export function getApiBusiness(): string | null {
  return businessId;
}

export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler;
}

function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)eb_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    const present = values.filter((v) => v !== undefined && v !== null && v !== '');
    if (present.length) params.set(key, present.map(String).join(','));
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'The request could not be processed.',
  401: 'Your session has ended. Please sign in again.',
  403: 'You do not have access to this.',
  404: 'We could not find what you were looking for.',
  409: 'This conflicts with a recent change. Refresh and try again.',
  413: 'That file or request is too large.',
  429: 'Too many requests. Please wait a moment and try again.',
};

async function toError(response: Response): Promise<ApiError> {
  let payload: (ApiResponse<unknown> & { requestId?: string }) | null = null;
  try {
    payload = (await response.json()) as ApiResponse<unknown> & { requestId?: string };
  } catch {
    // Non-JSON error bodies fall back to a generic message.
  }
  const error = payload && !payload.success ? payload.error : null;
  return new ApiError(
    response.status,
    error?.code ?? (response.status >= 500 ? 'INTERNAL_ERROR' : 'BAD_REQUEST'),
    error?.message ?? FALLBACK_MESSAGES[response.status] ?? 'Something went wrong. Please try again.',
    error?.fields ?? {},
    payload?.requestId ?? null,
  );
}

async function ensureCsrf(): Promise<string | null> {
  let token = csrfToken();
  if (!token) {
    await fetch('/api/auth/me', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    }).catch(() => undefined);
    token = csrfToken();
  }
  return token;
}

async function send(path: string, options: RequestOptions, retried = false): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ accept: 'application/json' });
  if (businessId) headers.set('x-business-id', businessId);
  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(options.body);
  }
  if (method !== 'GET') {
    const token = await ensureCsrf();
    if (token) headers.set('x-csrf-token', token);
  }
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach easiibill. Check your connection and try again.');
  }
  if (response.status === 403 && method !== 'GET' && !retried) {
    const clone = response.clone();
    const payload = (await clone.json().catch(() => null)) as { error?: { code?: string } } | null;
    if (payload?.error?.code === 'CSRF_INVALID') {
      document.cookie = 'eb_csrf=; Max-Age=0; path=/';
      return send(path, options, true);
    }
  }
  return response;
}

async function handle(path: string, response: Response): Promise<Response> {
  if (response.ok) return response;
  const error = await toError(response);
  if (response.status === 401 && !path.startsWith('/api/auth/')) unauthorizedHandler?.();
  throw error;
}

/** JSON request returning the `data` of the response envelope. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await handle(path, await send(path, options));
  const payload = (await response.json()) as ApiResponse<T>;
  return payload.success ? payload.data : (undefined as T);
}

/** Like `api` but also returns the server's user-facing message. */
export async function apiWithMessage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; message: string | null }> {
  const response = await handle(path, await send(path, options));
  const payload = (await response.json()) as ApiResponse<T>;
  return payload.success
    ? { data: payload.data, message: payload.message ?? null }
    : { data: undefined as T, message: null };
}

function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall through to the plain filename.
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? null;
}

/** Binary download (PDF, CSV, ZIP) with the same auth headers as JSON calls. */
export async function apiBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await handle(path, await send(path, options));
  return { blob: await response.blob(), filename: filenameFrom(response.headers.get('content-disposition')) };
}

export function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fields : {};
}
